import React from 'react';
import { Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import { SearchAutocomplete } from './components/SearchAutocomplete';
import styled from 'styled-components';
import { FooterSeparator } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { useColorTheme } from '@hooks';
import { filterDuplicateDocuments } from '@utils/usefulFuncs';
import { TransparentFlatButton } from '@ui/buttons';
import { extractDocTypes, fetchDocumentsForTags, getDocCategory, getDocTypeDisplayValue } from '@utils/doc';
import { formatTagResponseData } from '@features/query/form/TagInput/helpers';
import {
  assembleReportingPeriodTypes,
  REPORTING_PERIOD_AVAILABLE_TYPES,
  ReportingPeriodFilter,
} from './components/ReportingPeriodFilter';

const CompanyTagSearchInputWrapper = styled.div`
  max-width: 500px;
`;

const SelectableDocumentsRowWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content;
  width: max-content;
`;
const ResultWrapper = styled.div`
  display: inline-block;

  &:last-child {
    margin-bottom: 0px;
  }

  margin-bottom: 8px;
  margin-right: 16px;

  padding: 12px 16px;
  background: ${({ theme }) => convertHexToRGBA(theme.themeColors.lowContrastBorder, 1)};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.05)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};

    color: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};

    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  svg {
    height: 18px;
    width: 18px;
    margin-right: 8px;
    margin-left: -4px;
  }
`;

const FilterButtonWrapper = styled.div`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;
  min-width: 66px;
  text-align: center;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.1)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 1)};

    * {
      color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;

const FiltersButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`;

const DocumentTypeButton = ({ value, activeDocumentType, onClick }) => {
  const isActive = value === activeDocumentType;
  return (
    <FilterButtonWrapper onClick={() => onClick(value)} className={isActive ? 'selected' : ''}>
      <Body5 bold>{getDocTypeDisplayValue(value)}</Body5>
    </FilterButtonWrapper>
  );
};

const Result = ({ data, onSelect, onRemoveSelect, selectedDocs }) => {
  const { id, filename } = data;
  const isSelected = selectedDocs?.filter((d) => d.id === id).length > 0;

  const handleClick = () => {
    isSelected ? onRemoveSelect(data) : onSelect(data);
  };

  return (
    <ResultWrapper className={isSelected ? 'selected' : ''} onClick={handleClick}>
      <Body5>{filename}</Body5>
    </ResultWrapper>
  );
};

const useTagSearch = (tag) => {
  const [results, setResults] = React.useState(null);

  const getResults = React.useCallback(async (params) => {
    const { tag } = params;
    // Route by company vs tag (company_ids vs doc_tag_ids) and fetch both the
    // company's filings and the user's uploads — same util the allCompany mode
    // and TickerSelect use, so a company result actually filters to that company.
    const docs = await fetchDocumentsForTags([tag]);
    setResults(filterDuplicateDocuments(docs || []));
  }, []);

  React.useEffect(() => {
    setResults(null);
    getResults({ tag });
  }, [tag]);

  return { results, getResults };
};

const DocSelection = ({ tag, selectedDocs, onSelect, onRemoveSelect }) => {
  const colorTheme = useColorTheme();

  const { results } = useTagSearch(tag);

  const [activeDocumentType, setActiveDocumentType] = React.useState(null);
  const [documentTypes, setDocumentTypes] = React.useState([]);

  const [reportingPeriodFilterValues, setReportingPeriodFilterValues] = React.useState({});
  const [reportingPeriodTypes, setReportingPeriodTypes] = React.useState([]);

  const [filteredResults, setFilteredResults] = React.useState(null);

  const noneType = 'None';

  // Update document types and reporting period types when results change
  React.useEffect(() => {
    if (results) {
      setDocumentTypes(extractDocTypes(results));
      const nextReportingPeriodTypes = assembleReportingPeriodTypes(results);
      setReportingPeriodTypes(nextReportingPeriodTypes);
      setReportingPeriodFilterValues({ quarter: '', month: '', year: '' });
      setActiveDocumentType(null);
      setFilteredResults(sortByDate(results));
    }
  }, [results]);

  const sortByDate = (docs) => {
    return docs.sort((a, b) => new Date(b.report_date || b.published_at) - new Date(a.report_date || a.published_at));
  };

  const applyFilters = (docs, docType, periodFilters) => {
    let filtered = docs;

    // Filter by document type
    if (docType) {
      filtered = filtered.filter((d) => getDocCategory(d)?.toLowerCase() === docType.toLowerCase());
    }

    // Filter by reporting period
    if (periodFilters.quarter) {
      filtered = filtered.filter((doc) => doc.fiscal_quarter === parseInt(periodFilters.quarter, 10));
    } else if (periodFilters.month) {
      filtered = filtered.filter((doc) => doc.fiscal_month === parseInt(periodFilters.month, 10));
    } else if (periodFilters.year) {
      filtered = filtered.filter((doc) => doc.fiscal_year === parseInt(periodFilters.year, 10));
    }

    return sortByDate(filtered);
  };

  const handleDocumentTypeClick = (clickedDocumentType) => {
    const nextDocType = clickedDocumentType === activeDocumentType ? null : clickedDocumentType;
    setActiveDocumentType(nextDocType);
    setFilteredResults(applyFilters(results, nextDocType, reportingPeriodFilterValues));
  };

  const handleReportingPeriodChange = (type, value) => {
    const nextFilters = { ...reportingPeriodFilterValues, [type]: value };
    setReportingPeriodFilterValues(nextFilters);
    setFilteredResults(applyFilters(results, activeDocumentType, nextFilters));
  };

  const selectAll = () => {
    onSelect(filteredResults || results);
  };

  const unselectAll = () => {
    onRemoveSelect(filteredResults || results);
  };

  const displayResults = filteredResults || results;

  return (
    <Container>
      <Container>
        <Body5>Results for "{tag.name}"</Body5>
      </Container>
      <Container>
        {!results && (
          <Container top={48} left={8}>
            <Container row verticallyCenter>
              <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
              <Container left={16}>
                <Body5 bold>Searching...</Body5>
              </Container>
            </Container>
          </Container>
        )}
        {!results ? null : results.length === 0 ? (
          <Container top={16}>
            <Body5>No documents match the filter criteria</Body5>
          </Container>
        ) : (
          <>
            {documentTypes?.length > 0 && (
              <Container top={24}>
                <Container bottom={12}>
                  <Body5>Document Types</Body5>
                </Container>
                <FiltersButtonWrapper>
                  {documentTypes.map((v) => (
                    <DocumentTypeButton
                      key={v}
                      value={v}
                      activeDocumentType={activeDocumentType}
                      onClick={handleDocumentTypeClick}
                    />
                  ))}
                </FiltersButtonWrapper>
              </Container>
            )}
            {Object.values(reportingPeriodTypes).some((v) => v.length > 0) && (
              <Container top={24}>
                <Container bottom={12}>
                  <Body5>Reporting Periods</Body5>
                </Container>
                <FiltersButtonWrapper>
                  {REPORTING_PERIOD_AVAILABLE_TYPES.map((reportingPeriodType) =>
                    reportingPeriodTypes[reportingPeriodType]?.length > 0 ? (
                      <ReportingPeriodFilter
                        key={reportingPeriodType}
                        reportingPeriodType={reportingPeriodType}
                        options={reportingPeriodTypes[reportingPeriodType]}
                        value={reportingPeriodFilterValues[reportingPeriodType] || noneType}
                        onChange={handleReportingPeriodChange}
                      />
                    ) : null
                  )}
                </FiltersButtonWrapper>
              </Container>
            )}
            <Container top={24}>
              <Container bottom={28} row>
                <TransparentFlatButton onClick={unselectAll}>Unselect All</TransparentFlatButton>
                <Container left={8}>
                  <TransparentFlatButton onClick={selectAll}>Select All</TransparentFlatButton>
                </Container>
              </Container>
              <SelectableDocumentsRowWrapper>
                {displayResults.map((r, i) => (
                  <Result
                    key={i}
                    data={r}
                    onSelect={onSelect}
                    onRemoveSelect={onRemoveSelect}
                    selectedDocs={selectedDocs}
                  />
                ))}
              </SelectableDocumentsRowWrapper>
            </Container>
          </>
        )}
      </Container>
    </Container>
  );
};

export const CompanyTagDocumentSelection = ({ selectedDocs, onSelect, onRemoveSelect }) => {
  const [companyTag, setCompanyTag] = React.useState(null);

  const handleCompanyTagSelect = (v) => {
    setCompanyTag(formatTagResponseData(v));
  };

  return (
    <Container>
      <CompanyTagSearchInputWrapper>
        <Container bottom={8}>
          <Body5>Search Company / Tags</Body5>
        </Container>
        <SearchAutocomplete onSelect={handleCompanyTagSelect} onlyCompanyTags />
      </CompanyTagSearchInputWrapper>
      <Container top={24}>
        <FooterSeparator></FooterSeparator>
      </Container>

      {companyTag !== null && (
        <DocSelection
          tag={companyTag}
          selectedDocs={selectedDocs}
          onSelect={onSelect}
          onRemoveSelect={onRemoveSelect}
        />
      )}
    </Container>
  );
};
